import { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ToolCallCard } from './ToolCallCard';
import { SubAgentCard } from './SubAgentCard';
import { buildEventTree, type OrchestratorEvent } from './useOrchestratorStream';

interface ExecutionTimelineProps {
  events: OrchestratorEvent[];
  streamingText: string;
  isRunning: boolean;
}

export const ExecutionTimeline = ({ events, streamingText, isRunning }: ExecutionTimelineProps) => {
  const { topLevel, subAgentEvents } = useMemo(() => buildEventTree(events), [events]);

  const renderedCallIds = new Set<string>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5 }}>
      {topLevel.map((evt) => {
        switch (evt.type) {
          case 'thinking':
            return (
              <Box key={evt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Orchestrator thinking...
                </Typography>
              </Box>
            );

          case 'tool_call': {
            const toolName = evt.data.toolName as string;

            // If this tool call is a sub-agent invocation, render it as SubAgentCard.
            const matchingSubAgentEvents = [...subAgentEvents.entries()].find(
              ([, evts]) => evts.some((e) => e.type === 'sub_agent_started')
            );

            if (matchingSubAgentEvents) {
              const [callId, callEvents] = matchingSubAgentEvents;
              if (!renderedCallIds.has(callId)) {
                renderedCallIds.add(callId);
                return (
                  <SubAgentCard
                    key={`sub-${callId}`}
                    callId={callId}
                    events={callEvents}
                    allEvents={subAgentEvents}
                  />
                );
              }
            }

            return (
              <ToolCallCard
                key={evt.id}
                toolName={toolName}
                args={evt.data.arguments as string}
              />
            );
          }

          case 'response_complete':
            return (
              <Box
                key={evt.id}
                sx={{
                  p: 1.5,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 14,
                }}
              >
                {evt.data.response as string}
              </Box>
            );

          default:
            return null;
        }
      })}

      {/* Render any sub-agent call groups not already shown inline */}
      {[...subAgentEvents.entries()]
        .filter(([callId]) => !renderedCallIds.has(callId))
        .map(([callId, callEvents]) => (
          <SubAgentCard
            key={`sub-${callId}`}
            callId={callId}
            events={callEvents}
            allEvents={subAgentEvents}
          />
        ))}

      {isRunning && streamingText && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'info.main',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 14,
          }}
        >
          {streamingText}
          <Box component="span" sx={{ animation: 'blink 1s step-end infinite', '@keyframes blink': { '50%': { opacity: 0 } } }}>|</Box>
        </Box>
      )}
    </Box>
  );
};
